import { SET_AUTO_APPLIED_VOUCHER } from 'actions/actionConstants'
import { fetchAccumulatedRatesForBedbankOffer } from 'actions/BedbankOfferActions'
import { initBedbankSession, updateCheckoutItem } from 'actions/CheckoutActions'
import { forceMetasearchPublicRate } from 'actions/MarketingAction'
import type { AppAction } from 'actions/types/ActionTypes'
import { bedbankHotelPriceChangeModalOpen, bedbankSoldOutModalOpen } from 'actions/UiActions'
import { trackClientEvent } from 'analytics/analytics'
import { isGhaDiscountPreviewPromo } from 'checkout/hooks/usePendingGhaDiscountPreview'
import { getQuotedUpgradeTotal } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { isBedbankAccommodationOfferView, isBedbankItem } from 'lib/checkout/checkoutUtils'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { isMetasearchDiscountEnabled } from 'lib/metasearch/metasearchDiscountContext'
import { createMetasearchDiscountVoucher } from 'lib/metasearch/metasearchVoucherApi'
import { isGhaPriceMatchedInCart } from 'lib/metasearch/ghaPriceMatch'
import { clearPendingGhaVoucher } from 'lib/metasearch/pendingGhaVoucher'
import { isMetasearchPublicRateActive } from 'lib/metasearch/metasearchPublicRate'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { getSourceForProperty } from 'selectors/offerSelectors'
import { SOURCE_TYPES } from 'constants/source'

const SUPPORTED_GHA_VOUCHER_OCCUPANCIES = new Set(['1', '2'])

export interface CreateGhaVoucherInput {
  accountType?: string
  /** Absent when Google's click carried no RATE-PLAN-ID; `rateRuleId` then carries the claim. */
  token?: string
  propertyId?: string
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
  mx?: string
  rateRuleId?: string
  /**
   * Required when dispatching from anywhere but the offer page: the fallback below derives
   * the source from the current route, which decides the voucher's CUG entitlement.
   */
  source?: string
  /** Google's displayed total and its currency, from the landing URL (GHA-585). */
  advertisedPrice?: number
  advertisedCurrency?: string
}

/**
 * `minted`: a voucher exists and is set as the auto-applied voucher.
 * `none`: nothing to apply; the booking proceeds at the rate on screen. Also returned for a
 * price-matched booking, which never applies the voucher.
 * `public_rate`: the service withheld the voucher because it flagged the shopper (GHA-404). The
 * session is now marked public-rate only and the caller must re-price before continuing.
 */
export type GhaVoucherOutcome = 'minted' | 'none' | 'public_rate'

function isSupportedGhaVoucherOccupancy(occupancy: Array<string>): boolean {
  return occupancy.length === 1 && SUPPORTED_GHA_VOUCHER_OCCUPANCIES.has(occupancy[0])
}

export function createGhaVoucher(input: CreateGhaVoucherInput): AppAction {
  return async(dispatch, getState): Promise<GhaVoucherOutcome> => {
    // GHA-585: a bare rate rule ID is enough — the service re-selects the property's current
    // discount for the leg it names. Only a click carrying neither identifier has nothing to mint
    // from.
    if (
      !isMetasearchDiscountEnabled() ||
      (!input.token && !input.rateRuleId) ||
      !input.propertyId ||
      !input.checkIn ||
      !input.checkOut ||
      !input.rooms?.length
    ) {
      return 'none'
    }

    const occupancy = serialiseOccupancy(input.rooms)
    if (!isSupportedGhaVoucherOccupancy(occupancy)) {
      return 'none'
    }

    const state = getState()
    // A flagged session never asks again: the retry after the price-change modal would only be
    // flagged a second time, and there is no discount to mint for it anyway.
    if (isMetasearchPublicRateActive(state.marketing.metasearchPublicRateMarkedAt)) {
      return 'none'
    }

    const source = input.source ?? getSourceForProperty(state, input.propertyId)
    const response = await createMetasearchDiscountVoucher({
      token: input.token,
      accountType: input.accountType,
      propertyId: input.propertyId,
      checkIn: input.checkIn,
      checkOut: input.checkOut,
      occupancy,
      region: state.geo.currentRegionCode,
      isCugRate: source.includes(SOURCE_TYPES.CUG_METASEARCH),
      mx: input.mx,
      rateRuleId: input.rateRuleId,
      advertisedPrice: input.advertisedPrice,
      advertisedCurrency: input.advertisedCurrency,
    })
    dispatch(clearAwaitingHotelSight(input.propertyId))

    if (response.applyPublicRate) {
      dispatch(markMetasearchPublicRate())
      return 'public_rate'
    }

    // TODO(GHA-661): price-matched rooms mint only so the service can flag the shopper. Applying the
    // voucher would discount Google's price twice; the unused voucher expires on its own.
    if (response.voucherId && !isGhaPriceMatchedInCart(getState().checkout.cart.items, input.propertyId)) {
      dispatch({
        type: SET_AUTO_APPLIED_VOUCHER,
        data: response.voucherId,
      })
      return 'minted'
    }
    return 'none'
  }
}

/** Hotel Sight has checked the shopper, so checkout leaves the rooms be. */
function clearAwaitingHotelSight(propertyId: string): AppAction {
  return (dispatch, getState) => {
    getState().checkout.cart.items
      .filter(isBedbankItem)
      .filter(item => item.offerId === propertyId && item.awaitingHotelSight)
      .forEach(item => dispatch(updateCheckoutItem({ ...item, awaitingHotelSight: undefined }, { checkoutOnly: true })))
  }
}

/** GHA-404: the service withheld the discount, so this session books at the public rate from now on. */
function markMetasearchPublicRate(): AppAction {
  return (dispatch) => {
    dispatch(forceMetasearchPublicRate())
    clearPendingGhaVoucher()
    trackClientEvent({
      subject: 'gha_public_rate',
      action: 'applied',
      category: 'metasearch',
      type: 'operational',
    })
  }
}

/**
 * GHA-404, at checkout: svc-order withheld the discount from a guest at submit, or a room sold at
 * Google's price has nothing left to check the shopper with. Mark the session public as a flagged
 * member's mint does, then reprice the cart through the checkout price-change modal.
 */
export function forcePublicRateAtCheckout(propertyId: string): AppAction {
  return (dispatch) => {
    dispatch(markMetasearchPublicRate())
    dispatch(applyPublicRateAtCheckout(propertyId))
  }
}

/**
 * GHA-404, checkout replay: the stashed mint ran after the shopper signed in at checkout and the
 * service flagged them. The cart's enquiry session for this property was created at the member
 * rate or Google's price, so refetch the rate at the public source (the marker has switched the
 * selectors), book a new session at that price as Book now on the offer page does, and show the
 * new price in the checkout price-change modal.
 */
export function applyPublicRateAtCheckout(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const items = state.checkout.cart.items.filter(isBedbankItem).filter(item => item.offerId === propertyId)
    const [item] = items
    const rooms = items.map(({ occupancy }) => occupancy).filter((occupancy): occupancy is App.Occupants => !!occupancy)
    if (!item || rooms.length !== items.length) return
    // Read before the preview is reset: the price the shopper was shown, the room less the GHA
    // discount preview they are now losing.
    const isLuxPlusBooking = !!getEnquirySubscriber(state)?.tier
    const itemViews = checkoutAccommodationOfferView(state).data
      .filter(isBedbankAccommodationOfferView)
      .flatMap(view => view.itemViews)
      .filter(view => view.item.sessionId === item.sessionId)
    const promo = state.checkout.promo
    const ghaPreviewDiscount = isGhaDiscountPreviewPromo(promo) ? promo?.voucherTotal ?? 0 : 0
    const shownPrice = Math.max(getQuotedUpgradeTotal(itemViews, isLuxPlusBooking) - ghaPreviewDiscount, 0)

    dispatch(fetchAccumulatedRatesForBedbankOffer(propertyId, rooms, item.checkIn, item.checkOut, true, async(result) => {
      const publicRate = result.status === 'success' ? (
        result.data.find(candidate => candidate.id === item.roomRateId) ??
        result.data.find(candidate => candidate.roomId === item.roomId && candidate.groupId === item.rateGroupId)
      ) : undefined
      // Re-enquiring the cart's session would send its old price, and svc-bedbank books any price
      // that clears margin, so the room would stay at Google's price.
      const latestState = getState()
      const session = publicRate && await initBedbankSession(dispatch, {
        propertyId,
        roomTypeId: publicRate.roomId,
        roomRateId: publicRate.id,
        token: publicRate.token,
        rateGroupId: publicRate.groupId,
        bedGroupId: publicRate.bedGroups[0]?.id,
        isFlightBundle: publicRate.isFlightBundle,
        csBooking: latestState.auth.account.isSpoofed,
        region: latestState.geo.currentRegionCode,
        rooms,
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        price: publicRate.totals.inclusive,
        memberInclusive: publicRate.totals.memberInclusive,
        propertyFees: publicRate.totals.propertyFees,
        subscriber: getEnquirySubscriber(latestState),
        source: getSourceForProperty(latestState, propertyId),
      }).catch(() => undefined)
      // With no public price to book, this room cannot be booked from here: the cart is still on the
      // member rate or Google's price, so it must not be paid for as it stands.
      if (!publicRate || !session || !('sessionId' in session)) {
        dispatch(bedbankSoldOutModalOpen())
        return
      }
      items.forEach(cartItem => dispatch(updateCheckoutItem({
        ...cartItem,
        sessionId: session.sessionId,
        roomRateId: publicRate.id,
        rateGroupId: publicRate.groupId,
        token: publicRate.token,
        bedGroupId: publicRate.bedGroups[0]?.id,
        awaitingHotelSight: undefined,
      }, { checkoutOnly: true })))
      dispatch(bedbankHotelPriceChangeModalOpen({
        // `newPrice` is unused for bedbank. A zero `oldPrice` (no item views yet) makes the modal read
        // the cart's totals instead.
        newPrice: 0,
        oldPrice: shownPrice,
        bedbankSessionId: session.sessionId,
        newStandardPrice: publicRate.totals.inclusive * items.length,
        newLuxPlusPrice: publicRate.totals.memberInclusive ? publicRate.totals.memberInclusive * items.length : undefined,
        newPropertyFees: (publicRate.totals.propertyFees ?? 0) * items.length,
        isLuxPlusBooking,
      }))
    }))
  }
}
